import { readonly, ref, toRaw } from 'vue';
import { debounce } from 'lodash';
import { unreadCount } from '../api/messages';
import { markAccessRequestsSeen, unseenAccessRequests } from '../api/policyholders';
import { latestOnly } from '../helpers/accessPages';
import { canAssignBrokers } from '../helpers/roles';
import { useAuthStore } from '../stores/auth';
import { useSocketStore } from '../stores/socketStore';
import { listenForMessages } from './useMessagesLive';

/**
 * Counts the sidebar shows beside a nav item.
 *
 * Messages counts the viewer's own threads with a policyholder message nobody
 * at the agency has opened; opening the thread clears it.
 */
const POLL_MS = 60000;

const getSubdomain = () => window.store?.subdomain || 'default';

const badges = ref({ messages: 0 });
let timer = null;
let stopListening = null;
let stopAccess = null;
let users = 0;
let viewingHolds = 0;

export const refreshHubBadges = async () => {
  try {
    const messages = await unreadCount();
    badges.value = { ...badges.value, messages };
  } catch {
    // A badge that cannot be read shows nothing rather than a stale number.
    badges.value = { ...badges.value, messages: 0 };
  }
};

const queryAccess = async () => {
  try {
    return viewingHolds > 0 ? await markAccessRequestsSeen() : await unseenAccessRequests();
  } catch {
    return 0;
  }
};

const applyAccess = (count) => {
  badges.value = { ...badges.value, access_requests: count };
};

const fetchAccess = latestOnly(queryAccess, applyAccess);

export const refreshAccessRequests = async () => {
  if (!canAssignBrokers(useAuthStore().userData)) return;
  await fetchAccess();
};

const joinAccess = (state) => {
  if (!state.socket) return;
  state.socket.emit('hub_access:subscribe', { domain: getSubdomain() });
  void refreshAccessRequests();
};

const handleCountsChanged = (state, payload) => {
  if (payload?.domain !== getSubdomain()) return;
  state.debouncedRefresh();
};

async function connectAccessSocket(state) {
  const socketStore = useSocketStore();
  await socketStore.connect();
  const rawSocket = toRaw(socketStore.socket);
  if (state.stopped) return;
  if (!rawSocket) return;
  state.socket = rawSocket;
  rawSocket.on('hub_access:counts_changed', state.onCountsChanged);
  rawSocket.on('connect', state.onConnect);
  if (rawSocket.connected) {
    joinAccess(state);
  }
}

function stopAccessRequests(state, interval) {
  state.stopped = true;
  clearInterval(interval);
  state.debouncedRefresh.cancel();
  if (!state.socket) return;
  state.socket.off('hub_access:counts_changed', state.onCountsChanged);
  state.socket.off('connect', state.onConnect);
}

function startAccessRequests() {
  if (!canAssignBrokers(useAuthStore().userData)) {
    return () => {};
  }

  void refreshAccessRequests();
  const interval = setInterval(refreshAccessRequests, POLL_MS);

  const state = {
    socket: null,
    stopped: false,
    debouncedRefresh: null,
    onCountsChanged: null,
    onConnect: null,
  };

  state.debouncedRefresh = debounce(() => {
    if (!state.stopped) {
      void refreshAccessRequests();
    }
  }, 300);

  state.onCountsChanged = (payload) => handleCountsChanged(state, payload);
  state.onConnect = () => joinAccess(state);

  void connectAccessSocket(state);

  return () => stopAccessRequests(state, interval);
}

export function viewingPolicyholders() {
  let released = false;
  viewingHolds += 1;
  void refreshAccessRequests();
  return () => {
    if (released) return;
    released = true;
    viewingHolds = Math.max(0, viewingHolds - 1);
  };
}

/** Live, with a slow poll behind it, while any sidebar is mounted; call `release` when it unmounts. */
export function useHubBadges() {
  users += 1;

  if (!timer) {
    stopAccess = startAccessRequests();
    void refreshHubBadges();
    timer = setInterval(refreshHubBadges, POLL_MS);
    stopListening = listenForMessages(refreshHubBadges);
  }

  const release = () => {
    users -= 1;
    if (users <= 0 && timer) {
      clearInterval(timer);
      stopListening?.();
      stopAccess();
      timer = null;
      stopListening = null;
      stopAccess = null;
      users = 0;
      badges.value = { ...badges.value, access_requests: 0 };
    }
  };

  return { badges: readonly(badges), release };
}
